import { useState, useEffect, useRef, useCallback } from 'react'
import { ticketsAPI } from '../services/api'

/**
 * Hook for managing ticket list data with polling, pagination, and filtering.
 * Uses the lightweight /tickets/list endpoint for efficient list display.
 *
 * @param {Object} options
 * @param {number|string|null} options.selectedTicketId - Currently selected ticket ID (ensures it's in response)
 * @returns {Object} Ticket list state and controls
 */
export const useTicketList = ({ selectedTicketId = null } = {}) => {
  // Core state
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Search/filter state
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Filter state with localStorage persistence
  const getStoredFilters = () => {
    try {
      const stored = localStorage.getItem('agentflow-list-filters')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (e) {
      console.warn('Failed to parse list filters from localStorage:', e)
    }
    return {
      ticketStatus: '',
      ticketType: '',
      showMyTickets: false,
      showAssignedToMe: false,
      showArchived: false,
      filterUser: '',
    }
  }

  const [filters, setFiltersState] = useState(() => getStoredFilters())

  const pendingRequestRef = useRef(null)
  const selectedTicketIdRef = useRef(selectedTicketId)

  // Keep selectedTicketIdRef in sync for use inside fetchTickets callbacks
  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId
  }, [selectedTicketId])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 500)
    return () => clearTimeout(timer)
  }, [query])

  // Save filters to localStorage
  const setFilters = useCallback((newFilters) => {
    const updatedFilters = typeof newFilters === 'function'
      ? newFilters(filters)
      : newFilters
    setFiltersState(updatedFilters)
    try {
      localStorage.setItem('agentflow-list-filters', JSON.stringify(updatedFilters))
    } catch (e) {
      console.warn('Failed to save list filters to localStorage:', e)
    }
    // Reset pagination when filters change
    setPage(1)
    setTickets([])
  }, [filters])

  // Fetch tickets
  const fetchTickets = useCallback(async (options = {}) => {
    const {
      pageNum = 1,
      append = false,
    } = options

    // Create request signature for deduplication
    const requestSignature = `list-${selectedTicketIdRef.current}-${pageNum}-${debouncedQuery}-${JSON.stringify(filters)}`

    // Prevent concurrent requests with same parameters
    if (pendingRequestRef.current === requestSignature) {
      return
    }

    pendingRequestRef.current = requestSignature

    try {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setLoading(true)
      }

      const hasActiveFilters = debouncedQuery || filters.ticketStatus || filters.ticketType ||
        filters.showAssignedToMe || filters.showMyTickets || filters.showArchived || filters.filterUser

      const response = await ticketsAPI.getTicketList({
        selectedTicketId: hasActiveFilters ? undefined : selectedTicketIdRef.current,
        query: debouncedQuery || undefined,
        page: pageNum,
        page_size: 20,
        status: filters.ticketStatus || undefined,
        ticket_type: filters.ticketType || undefined,
        assigned: filters.showAssignedToMe || undefined,
        created: filters.showMyTickets || undefined,
        archived_only: filters.showArchived || undefined,
        filter_user: filters.filterUser || undefined,
      })

      setError(null)

      // Update pagination state
      const pagination = response.pagination
      if (pagination) {
        setHasMore(pagination.has_next)
        setPage(pageNum)
      } else {
        setHasMore(false)
      }

      // Update tickets
      setTickets(prevTickets => {
        const newTickets = response.tickets || []

        // If appending (infinite scroll), merge with existing tickets
        if (append && prevTickets.length > 0) {
          const existingIds = new Set(prevTickets.map(t => t.id))
          const uniqueNewTickets = newTickets.filter(t => !existingIds.has(t.id))
          return [...prevTickets, ...uniqueNewTickets]
        }

        // If no previous tickets or not appending
        if (prevTickets.length === 0 || !append) {
          return newTickets
        }

        // Check if tickets have actually changed by comparing content_hash
        const prevTicketMap = new Map(prevTickets.map(ticket => [ticket.id, ticket]))
        const hasChanges = newTickets.some(newTicket => {
          const prevTicket = prevTicketMap.get(newTicket.id)
          if (!prevTicket) return true
          return prevTicket.content_hash !== newTicket.content_hash
        }) || prevTickets.length !== newTickets.length

        return hasChanges ? newTickets : prevTickets
      })

    } catch (err) {
      console.error('Failed to fetch ticket list:', err)

      if (err.response?.status === 401 || err.response?.status === 403) {
        pendingRequestRef.current = null
        return
      }

      setError(err)
    } finally {
      pendingRequestRef.current = null
      setLoading(false)
      setIsLoadingMore(false)
    }
  }, [debouncedQuery, filters])

  // Load more tickets (infinite scroll)
  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchTickets({ pageNum: page + 1, append: true })
    }
  }, [isLoadingMore, hasMore, page, fetchTickets])

  // Manual refresh
  const refresh = useCallback(() => {
    setPage(1)
    fetchTickets({ pageNum: 1 })
  }, [fetchTickets])

  // Patch a single row in place (status/role/modified_at) without a refetch.
  const patchTicketRow = useCallback((ticketId, fields) => {
    if (!ticketId || !fields) return
    setTickets(prev => {
      const idx = prev.findIndex(t => String(t.id) === String(ticketId))
      if (idx === -1) return prev
      const updated = [...prev]
      updated[idx] = {
        ...updated[idx],
        ...(fields.status !== undefined && { status: fields.status }),
        ...(fields.role !== undefined && { role: fields.role }),
        ...(fields.modified_at !== undefined && { modified_at: fields.modified_at }),
      }
      return updated
    })
  }, [])

  const prependTicket = useCallback((ticket) => {
    if (!ticket) return
    setTickets(prev => {
      if (prev.some(t => String(t.id) === String(ticket.id))) return prev
      return [ticket, ...prev]
    })
  }, [])

  // Fetch on mount and when filters or search query changes
  useEffect(() => {
    fetchTickets({ pageNum: 1 })
  }, [debouncedQuery, filters, fetchTickets])

  return {
    // Data
    tickets,
    loading,
    error,

    // Pagination
    hasMore,
    isLoadingMore,
    loadMore,

    // Search
    query,
    setQuery,

    // Filters
    filters,
    setFilters,

    // Actions
    refresh,
    patchTicketRow,
    prependTicket,
  }
}

export default useTicketList
